import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { setUserProperties, trackEvent, generateEventId } from '@/lib/tracking';
import { onContactLinkClick } from '@/lib/contactLinks';
import { PopupModal, useCalendlyEventListener } from 'react-calendly';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    contactNumber: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);

  // Fire book_consultation ONLY when Calendly confirms the booking. Not on
  // modal open, not on date selection, not on close. postMessage-driven.
  useCalendlyEventListener({
    onEventScheduled: () => {
      try {
        trackEvent('book_consultation', {
          source_page: location.pathname,
          event_id: generateEventId(),
        });
      } catch {
        /* tracking never breaks the user flow */
      }
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        contact_number: formData.contactNumber,
        service: formData.service,
        message: formData.message
      });

      if (response.data.success) {
        // GA4 + GTM + Clarity (PII-stripped). Fires only on backend success.
        setUserProperties({
          company_name: formData.company,
          last_service_enquired: formData.service,
          source: 'contact_form',
        });
        trackEvent('generate_lead', {
          form_name: 'contact',
          currency: 'ZAR',
          value: 1,
          event_id: generateEventId(),
          service: formData.service,
          has_company: Boolean(formData.company),
          has_phone: Boolean(formData.contactNumber),
        });

        toast.success("Thanks for reaching out. We'll get back to you shortly.");
        setFormData({
          name: '',
          email: '',
          company: '',
          contactNumber: '',
          service: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="contact-page pt-20">
      {/* Hero Section */}
      <section className="contact-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-white mb-6">
              Let's Start a<br />
              <span className="text-gradient">Conversation</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Whether you have a specific project in mind or just want to explore possibilities, we're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="contact-content py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">Get in Touch</h2>
                <p className="text-gray-400 leading-relaxed mb-8">
                  We typically respond within 24 hours. For urgent inquiries, please call us directly.
                </p>
              </div>

              <Card className="contact-info-card">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">Email</div>
                      <a href="mailto:mishaan@radiusconsulting.co.za" onClick={onContactLinkClick} className="text-gray-400 hover:text-red-500 transition-colors">
                        mishaan@radiusconsulting.co.za
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">Phone</div>
                      <a href="tel:0837770914" onClick={onContactLinkClick} className="text-gray-400 hover:text-red-500 transition-colors">
                        083 777 0914
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-600/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <div className="text-white font-semibold mb-1">Location</div>
                      <div className="text-gray-400">
                        Johannesburg, SA<br />
                        Remote-friendly
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hours-card">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4">Office Hours</h3>
                  <div className="space-y-2 text-sm mb-6">
                    <div className="flex justify-between text-gray-400">
                      <span>Monday - Friday</span>
                      <span className="text-white">9:00 - 18:00</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Saturday - Sunday</span>
                      <span className="text-gray-500">Closed</span>
                    </div>
                  </div>
                  
                  {/* Book a Call Button */}
                  <div className="pt-4 border-t border-gray-700">
                    <Button 
                      onClick={() => setIsCalendlyOpen(true)}
                      className="btn-primary w-full"
                    >
                      📅 Book a Call
                    </Button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Schedule a 15-minute intro call
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="contact-form-card">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-bold text-white mb-8">Send Us a Message</h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-white">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          placeholder="John Smith"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-white">Company</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          placeholder="Your Company"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="contactNumber" className="text-white">Contact Number</Label>
                        <Input
                          id="contactNumber"
                          type="tel"
                          value={formData.contactNumber}
                          onChange={(e) => handleChange('contactNumber', e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                          placeholder="+27 123 456 7890"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service" className="text-white">What are you interested in? *</Label>
                      <Select value={formData.service} onValueChange={(value) => handleChange('service', value)} required>
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="brand-strategy">Brand Strategy & Evolution</SelectItem>
                          <SelectItem value="growth-marketing">Growth Marketing</SelectItem>
                          <SelectItem value="creative">Creative Execution</SelectItem>
                          <SelectItem value="ai-content">AI-Powered Content</SelectItem>
                          <SelectItem value="digital">Digital Product Design</SelectItem>
                          <SelectItem value="retail">Retail Experience</SelectItem>
                          <SelectItem value="multiple">Multiple Services</SelectItem>
                          <SelectItem value="not-sure">Not Sure Yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-white">Tell us about your project *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 min-h-[150px]"
                        placeholder="What challenge are you trying to solve? What are your goals?"
                        required
                      />
                    </div>

                    <Button type="submit" size="lg" className="btn-primary w-full group" disabled={isSubmitting}>
                      <Send className="mr-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>

                    <p className="text-gray-500 text-sm text-center">
                      By submitting this form, you agree to our Privacy Policy and Terms of Service.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Calendly Popup Modal */}
      <PopupModal
        url="https://calendly.com/mishaan-radiusconsulting/15-minute-meeting-clone"
        onModalClose={() => setIsCalendlyOpen(false)}
        open={isCalendlyOpen}
        rootElement={document.getElementById('root')}
      />
    </div>
  );
};

export default Contact;
